import type { EstateItem, EstateStageJson } from '@types';

export interface StageValidationResult {
  errors: string[];
  warnings: string[];
  assignedCount: number;
  unassignedCodes: string[];
}

export function compareLotCodes(left: string, right: string): number {
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
}

export function assignLotsToStage(
  stages: EstateStageJson[],
  lotCodes: string[],
  targetStageIndex: number | null
): EstateStageJson[] {
  const selectedCodes = new Set(lotCodes.map(String));
  return stages.map((stage, stageIndex) => {
    const remainingCodes = (stage.lots ?? [])
      .map(String)
      .filter((code) => !selectedCodes.has(code));
    const assignedCodes =
      targetStageIndex === stageIndex ? [...remainingCodes, ...selectedCodes] : remainingCodes;

    return {
      ...stage,
      lots: Array.from(new Set(assignedCodes)).sort(compareLotCodes),
    };
  });
}

export function validateStageConfiguration(
  stages: EstateStageJson[],
  lots: EstateItem[]
): StageValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const validLotCodes = new Set(lots.map((lot) => String(lot.code)));
  const assignedTo = new Map<string, string[]>();
  const stageNames = new Map<string, number>();

  stages.forEach((stage, index) => {
    const name = stage.name.trim();
    if (!name) {
      errors.push(`Stage ${index + 1} needs a name.`);
    } else {
      const key = name.toLocaleLowerCase();
      stageNames.set(key, (stageNames.get(key) ?? 0) + 1);
    }

    for (const rawCode of stage.lots ?? []) {
      const code = String(rawCode).trim();
      if (!validLotCodes.has(code)) {
        errors.push(`${name || `Stage ${index + 1}`} contains unknown lot ${code}.`);
        continue;
      }
      const assignments = assignedTo.get(code) ?? [];
      assignments.push(name || `Stage ${index + 1}`);
      assignedTo.set(code, assignments);
    }
  });

  for (const [name, count] of stageNames) {
    if (count > 1) errors.push(`Stage name “${name}” is duplicated.`);
  }

  for (const [code, assignments] of assignedTo) {
    if (assignments.length > 1) {
      errors.push(`Lot ${code} is assigned to multiple stages: ${assignments.join(', ')}.`);
    }
  }

  const unassignedCodes = [...validLotCodes]
    .filter((code) => !assignedTo.has(code))
    .sort(compareLotCodes);

  if (lots.length > 0 && stages.length === 0) {
    warnings.push('No stages have been created. Lots will export without stage assignments.');
  } else if (unassignedCodes.length > 0) {
    warnings.push(
      `${unassignedCodes.length} lot${unassignedCodes.length === 1 ? '' : 's'} ` +
        `unassigned: ${unassignedCodes.join(', ')}.`
    );
  }

  return {
    errors,
    warnings,
    assignedCount: lots.length - unassignedCodes.length,
    unassignedCodes,
  };
}
